import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

import { signupHrefForPath } from '@/lib/presetFromPath';

import AutoPlayVideo from '@/components/media/AutoPlayVideo';
import { HappyUsers } from '@/components/landing/HappyUsers';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';

const MEDIA = 'https://media.easy-peasy.ai/landing/video';
const LANDING = 'https://media.easy-peasy.ai/landing';

const STEPS: Array<[string, string, string]> = [
  [
    '01',
    'You describe it',
    'One sentence is enough — or add your own photos, clips and brand notes.',
  ],
  [
    '02',
    'The agent produces it',
    'It writes the script, generates every scene, picks a voice, adds music and captions.',
  ],
  [
    '03',
    'You fine-tune it',
    'The finished project opens in the editor — swap scenes, retime captions, regenerate anything.',
  ],
];

const CAPABILITIES = [
  'Script and scenes from a single prompt',
  'Voiceover in lifelike voices, 40+ languages',
  'Music, sound and synced captions',
  'Use your own photos and footage',
  'Vertical, square or widescreen',
  'Full timeline editor to finish the job',
];

interface VideoAgentLandingProps {
  seoTitle: string;
  seoDescription?: string;
}

// Marketing landing served to logged-out visitors on /create-video — the
// logged-in experience (the actual agent form) stays in create-video.tsx.
const VideoAgentLanding = ({
  seoTitle,
  seoDescription,
}: VideoAgentLandingProps) => {
  const { t } = useLocale('ai-videos');
  const router = useRouter();
  // Carries the Video Agent intent through signup so the dashboard deep-links
  // back to /create-video instead of dropping the user on the dashboard.
  const signupHref = signupHrefForPath(router.asPath);

  return (
    <main className='relative bg-[#07070C]'>
      <Seo
        templateTitle={seoTitle}
        description={
          seoDescription ??
          t(
            'Describe your idea and the Video Agent writes the script, generates the scenes, adds voiceover, music and captions — a finished video from one sentence.',
          )
        }
        image='https://media.easy-peasy.ai/landing/video/og-video-agent.jpg'
      />

      {/* Hero */}
      <section className='relative overflow-hidden bg-[#050509]'>
        <video
          className='absolute inset-0 h-full w-full object-cover'
          src={`${MEDIA}/filmset.mp4`}
          poster={`${MEDIA}/filmset-poster.jpg`}
          autoPlay
          muted
          loop
          playsInline
          preload='auto'
        />
        <div className='pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_65%_60%_at_50%_45%,rgba(5,5,9,0.78),rgba(5,5,9,0.4)_65%,rgba(5,5,9,0.2))]' />
        <div className='pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#050509]/90 to-transparent' />
        <div className='pointer-events-none absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-[#050509] to-transparent' />

        <div className='absolute top-0 left-0 w-full z-30'>
          <LandingPageNavigation variant='light' forceDark />
        </div>

        <div className='relative z-20 mx-auto flex min-h-[92svh] max-w-4xl flex-col items-center justify-center px-4 pt-32 pb-24 text-center sm:px-6'>
          <div className='mb-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/30 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-slate-200 backdrop-blur-md sm:text-sm'>
            <span className='rounded-full bg-indigo-500 px-2 py-0.5 text-[10px] font-bold text-white'>
              {t('BETA')}
            </span>
            {t('The Video Agent')}
          </div>

          <h1 className='text-5xl font-extrabold tracking-tight text-white drop-shadow-2xl sm:text-6xl md:text-7xl lg:text-[5rem]'>
            {t('One sentence in.')}
            <span className='mt-1 block bg-gradient-to-r from-indigo-300 via-violet-300 to-fuchsia-300 bg-clip-text pb-2 text-transparent'>
              {t('A finished video out.')}
            </span>
          </h1>

          <p className='mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-slate-200 drop-shadow-lg md:text-xl'>
            {t(
              'Your production crew, automated: the agent writes the script, generates the scenes, records the voiceover, scores the music and syncs the captions.',
            )}
          </p>

          <div className='mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row'>
            <Link
              href={signupHref}
              className='group inline-flex h-[58px] min-w-[230px] items-center justify-center gap-2 rounded-full bg-indigo-500 px-8 text-lg font-bold text-white shadow-[0_8px_40px_-8px_rgba(99,102,241,0.7)] transition-all hover:bg-indigo-400 hover:shadow-[0_8px_50px_-6px_rgba(99,102,241,0.9)]'
            >
              {t('Make my video — free')}
              <span
                aria-hidden='true'
                className='transition-transform group-hover:translate-x-1'
              >
                →
              </span>
            </Link>
            <a
              href='#example'
              className='inline-flex h-[58px] min-w-[180px] items-center justify-center rounded-full border border-white/25 bg-black/25 px-8 text-lg font-semibold text-white backdrop-blur-md transition-all hover:border-white/45 hover:bg-black/40'
            >
              {t('See a real result')}
            </a>
          </div>

          <div className='mt-8 flex justify-center'>
            <HappyUsers fromLandingPage={true} className='items-center' />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className='relative py-20 lg:py-28'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <div className='mb-14 text-center'>
            <h2 className='text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
              {t('Three steps. Zero timeline anxiety.')}
            </h2>
          </div>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5'>
            {STEPS.map(([number, title, description]) => (
              <div
                key={number}
                className='rounded-2xl border border-white/10 bg-white/[0.04] p-8'
              >
                <div className='text-sm font-bold tracking-widest text-indigo-300'>
                  {number}
                </div>
                <h3 className='mt-3 text-xl font-bold text-white'>
                  {t(title)}
                </h3>
                <p className='mt-2 leading-relaxed text-slate-400'>
                  {t(description)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real example + capabilities */}
      <section
        id='example'
        className='relative border-y border-white/[0.06] bg-white/[0.02] py-20 lg:py-28'
      >
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2'>
            <div className='order-2 flex justify-center lg:order-1'>
              <div className='relative w-full max-w-[300px] overflow-hidden rounded-3xl border border-white/10 bg-black shadow-2xl'>
                <div style={{ aspectRatio: '9/16' }}>
                  <AutoPlayVideo
                    src={`${LANDING}/social-story-video-example.mp4`}
                    poster={`${LANDING}/social-story-video-example-poster.jpg`}
                    className='h-full w-full object-cover'
                  />
                </div>
                <span className='absolute bottom-3 left-3 rounded-full bg-black/55 px-3 py-1 text-xs font-semibold text-white backdrop-blur-md'>
                  {t('Made by the Video Agent')}
                </span>
              </div>
            </div>
            <div className='order-1 lg:order-2'>
              <h2 className='text-3xl font-extrabold tracking-tight text-white sm:text-4xl'>
                {t('This video started as one sentence')}
              </h2>
              <p className='mt-4 text-lg leading-8 text-slate-300'>
                {t(
                  'Script, scenes, narration, music and captions — all generated, then polished in the built-in editor. That is the whole workflow.',
                )}
              </p>
              <ul className='mt-8 space-y-4'>
                {CAPABILITIES.map((capability) => (
                  <li
                    key={capability}
                    className='flex items-center text-slate-300'
                  >
                    <svg
                      className='mr-3 h-5 w-5 shrink-0 text-emerald-400'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M5 13l4 4L19 7'
                      />
                    </svg>
                    {t(capability)}
                  </li>
                ))}
              </ul>
              <Link
                href={signupHref}
                className='mt-10 inline-flex h-[54px] items-center justify-center rounded-full bg-indigo-500 px-10 text-lg font-bold text-white shadow-[0_8px_40px_-8px_rgba(99,102,241,0.7)] transition-all hover:bg-indigo-400'
              >
                {t('Start my first video')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className='relative py-20 lg:py-28'>
        <div className='mx-auto max-w-5xl px-4 text-center sm:px-6'>
          <h2 className='text-3xl font-extrabold tracking-tight text-white md:text-4xl'>
            {t('What will you brief it on?')}
          </h2>
          <div className='mt-8 flex flex-wrap justify-center gap-3'>
            {[
              'Product demos',
              'Explainers',
              'Social stories',
              'Real estate tours',
              'Documentaries',
              'Ads & promos',
              'Listicles',
              'Educational shorts',
            ].map((useCase) => (
              <span
                key={useCase}
                className='rounded-full border border-white/10 bg-white/[0.04] px-5 py-2 font-medium text-slate-300'
              >
                {t(useCase)}
              </span>
            ))}
          </div>
          <Link
            href={signupHref}
            className='mt-12 inline-flex h-[58px] items-center justify-center rounded-full bg-white px-10 text-lg font-bold text-slate-900 shadow-xl transition-all hover:bg-slate-100'
          >
            {t('Try the Video Agent free')}
          </Link>
        </div>
      </section>

      <div className='dark'>
        <Footer variant='dark' />
      </div>
    </main>
  );
};

export default VideoAgentLanding;
